/**
 * How one seat (root, member, self, advisor) gets one capability, decided by the seat's
 * probed profile and the run's environment, never by the model's vendor: the provider
 * delivers it natively, a Foundry hosted tool delivers it, V5's harness supplies it, V5
 * emulates a behaviour the provider lacks, or nobody does and the seat is refused.
 *
 * This is the one rule (workflows/Anthropics/V5-Multi-Agent-Parity-Plan.md Part A §0 and
 * §4.1, PR A1). The Foundry compiler, the run-time checker and the catalog's seat table
 * all call `seatDelivery`, so what the report says, what the site shows and what the run
 * does cannot drift. Claude Managed Agents is the reference and delivers everything
 * natively; the loop engines read the profile.
 */
export const DELIVERIES = ["native", "hosted", "platform", "emulated", "none"];
export const SEAT_CAPABILITIES = [
    "functions",
    "web_search",
    "web_fetch",
    "mcp",
    "bash",
    "file_tools",
    "memory",
    "structured_output",
    "image_input",
    "file_input",
    "compaction",
    "coordinator",
    "workflows",
    "inline_agents",
    "advisor",
];
/** The profile key each capability reads; null where the answer comes from the environment alone. */
export const SEAT_PROFILE_KEYS = {
    functions: "functions",
    web_search: "webSearch",
    web_fetch: null,
    mcp: "mcp",
    bash: null,
    file_tools: null,
    memory: null,
    structured_output: "structuredOutput",
    image_input: "imageInput",
    file_input: "fileInput",
    compaction: "compaction",
    coordinator: null,
    workflows: null,
    inline_agents: null,
    advisor: null,
};
/** One sentence per delivery, for report notes and the seat table: the same words everywhere. */
export const DELIVERY_NOTES = {
    native: "the model provides it natively",
    hosted: "hosted by Microsoft Foundry",
    platform: "V5's harness supplies it",
    emulated: "V5 emulates it",
    none: "not available on this deployment",
};
/** A string capability, or null when absent or "unmeasured" (the profile loader's `capStr` rule). */
function capStr(capabilities, key) {
    const value = capabilities[key];
    return typeof value === "string" && value !== "unmeasured" ? value : null;
}
const answer = (delivery, unmeasured = null) => ({
    delivery,
    unmeasured,
});
/** `native` when the profile says so; otherwise `none`, naming the key when it was never measured. */
function byProfile(capabilities, key, yes) {
    const value = capStr(capabilities, key);
    if (value === yes)
        return answer("native");
    return answer("none", value === null ? key : null);
}
export function seatDelivery(capabilities, capability, ctx) {
    if (ctx.engine === "cma_native")
        return answer("native");
    const conversations = ctx.transport === "conversations";
    switch (capability) {
        case "functions":
            return byProfile(capabilities, "functions", "native");
        case "web_search": {
            const ws = capStr(capabilities, "webSearch");
            const asks = ctx.policy === "ask";
            if (ws === "hosted" && !asks && conversations)
                return answer("hosted");
            // An approval gate, the stateless chassis, or a profile without hosted search all need
            // the harness's function, which only answers when the search backend is configured.
            if (ctx.webSearchBackend && (ws === "platform" || asks || !conversations))
                return answer("platform");
            return answer("none", ws === null && !asks && conversations ? "webSearch" : null);
        }
        case "web_fetch":
            return answer("platform");
        case "mcp": {
            const mcp = capStr(capabilities, "mcp");
            // `flaky` is not hosted: the profile fact drives the fallback, never a hard refusal by name.
            if (mcp === "hosted" && conversations)
                return answer("hosted");
            if (ctx.harnessMcp)
                return answer("platform");
            return answer("none", mcp === null && conversations ? "mcp" : null);
        }
        case "bash":
            return ctx.sandbox ? answer(conversations ? "hosted" : "platform") : answer("none");
        case "file_tools":
            return ctx.virtualFs ? answer("platform") : answer("none");
        case "memory":
        case "coordinator":
        case "workflows":
        // The coordinator runs an inline agent as it runs a roster one: a thread on the lead's
        // model, tools, MCP servers and skills, with the system prompt the lead or the plan wrote.
        case "inline_agents":
        case "advisor":
            return answer("platform");
        case "structured_output": {
            const so = capStr(capabilities, "structuredOutput");
            if (so === "native")
                return answer("native");
            if (so === "fenced")
                return answer("platform");
            return answer("none", so === null ? "structuredOutput" : null);
        }
        case "image_input":
            return byProfile(capabilities, "imageInput", "native");
        case "file_input": {
            // The profiler records `{ pdf, image }`; a PDF read natively is what file input means.
            const value = capabilities.fileInput;
            if (typeof value !== "object" || value === null)
                return byProfile(capabilities, "fileInput", "native");
            return value.pdf === "native"
                ? answer("native")
                : answer("none");
        }
        case "compaction":
            // Server compaction rides on Foundry conversations; the stateless chassis has no
            // compaction of its own yet, so it answers `none` there rather than claim a cell.
            if (!conversations)
                return answer("none");
            return byProfile(capabilities, "compaction", "native");
    }
}
//# sourceMappingURL=seat-delivery.js.map